import React, { useState, useEffect } from 'react'
import PartiallyApprovedTimeOffForm from './PartiallyApprovedTimeOffForm'
import ButtonGroup from './ButtonGroup'
import HoldValidationMessage from './HoldValidationMessage'
import { canHoldRequest } from './holdValidation'
import { APPLY_REQUEST_STATUS_HEADING } from './radioOptions'
import StatusRadioOptions from './StatusRadioOptions'
import { ButtonStateConfig } from './buttonStateUtils'
import { isISODateInPast } from '../../../utils/date'

interface TableRow {
  id: string
  date: string
  day: string
  times: string
  requestedHours: string
}

interface ApprovedTimeOffFormProps {
  onSubmit?: (data: {
    requestId: string
    status: string
    comments: string
  }) => void
  onCancel?: () => void
  onStatusChange?: (status: string) => void
  numberOfDates?: number
  tableRows?: TableRow[]
  dateRequested?: string
  isRetracted?: boolean
  activeForm?: 'approved' | 'denied' | 'hold' | 'partiallyapproved' | 'returned'
  isDisabledDueToDate?: boolean
  buttonStateConfig?: ButtonStateConfig
  requestId: string | undefined
  enableOverrideSubmitButton?: boolean
  disableOverrideSubmitButton?: boolean
  toTypeCd?: string
  disableEndDate?: string
  /** True once the approver has explicitly picked "Approved"; distinguishes an
   * intentional selection from the default activeForm='approved' initial state. */
  isApprovedSelected?: boolean
}

const ApprovedTimeOffForm: React.FC<ApprovedTimeOffFormProps> = ({
  onSubmit,
  onCancel,
  onStatusChange,
  numberOfDates = 1,
  tableRows = [],
  dateRequested = '',
  isRetracted = false,
  activeForm = 'approved',
  isDisabledDueToDate = false,
  buttonStateConfig,
  requestId = '',
  enableOverrideSubmitButton = false,
  disableOverrideSubmitButton = false,
  toTypeCd,
  disableEndDate,
  isApprovedSelected = false,
}) => {
  const [approverComments, setApproverComments] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [showPartialForm, setShowPartialForm] = useState(false)

  const isMultipleDates = numberOfDates > 1

  // Calculate if Hold can be applied based on dateRequested
  const canHold = canHoldRequest(dateRequested, toTypeCd)

  const getStatusFromActiveForm = (form?: string): string => {
    if (form === 'partiallyapproved') return 'partiallyapproved'
    if (form === 'returned') return 'returned'
    if (form === 'denied') return 'denied'
    if (form === 'hold') return 'hold'
    // For submitted requests (activeForm='approved' by default), return empty to show unselected
    return ''
  }

  // Sync internal state with parent's activeForm when it changes
  useEffect(() => {
    const statusFromForm = getStatusFromActiveForm(activeForm)
    setSelectedStatus(
      statusFromForm || (isApprovedSelected ? 'approved' : '')
    )
    setShowPartialForm(activeForm === 'partiallyapproved')
  }, [activeForm, isApprovedSelected])

  /**
   * Returns true if the given date is before today.
   * Time portion is ignored.
   */
  const isPastDate = (date?: string) => {
    return isISODateInPast(date)
  }

  // Disable when BOTH start and end dates are before today — retractions/partial
  // retractions must remain processable regardless of past dates, so they're exempt.
  const areBothDatesInPast =
    !isRetracted && isPastDate(dateRequested) && isPastDate(disableEndDate)

  /**
   * Handle status radio button selection
   */
  const handleStatusChange = (status: string) => {
    setSelectedStatus(status)

    if (status === 'partiallyapproved') {
      setShowPartialForm(true)
    } else {
      setShowPartialForm(false)
    }

    if (onStatusChange) {
      onStatusChange(status)
    }
  }

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({
        requestId,
        status: selectedStatus,
        comments: approverComments,
      })
    }
  }

  const isOverrideForm = buttonStateConfig?.showOverrideForm === true

  const submitDisabled =
    isDisabledDueToDate ||
    areBothDatesInPast ||
    !selectedStatus ||
    (isOverrideForm && disableOverrideSubmitButton)

  const submitEnabled =
    isOverrideForm && enableOverrideSubmitButton

  return (
    <div className="px-6! pt-0! pb-6!">
      <p className="p4 -medium text-[#0D1C3D] m-0! pb-1!">
        {APPLY_REQUEST_STATUS_HEADING.text}
      </p>

      {/* Status Radio Buttons */}
      <div className="space-y-4 mb-8 pb-6!">
        <StatusRadioOptions
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          buttonStateConfig={buttonStateConfig}
          canHold={canHold}
          isRetracted={isRetracted}
          numberOfDates={numberOfDates}
          disableAllOptions={isDisabledDueToDate || areBothDatesInPast}
        />
      </div>

      {/* Partially Approved Form */}
      {showPartialForm && isMultipleDates && (
        <div className="mb-6 pt-6 border-t">
          <PartiallyApprovedTimeOffForm
            tableRows={tableRows}
            onStatusChange={handleStatusChange}
            onCancel={onCancel}
            onSubmit={onSubmit}
            toTypeCd={toTypeCd}
          />
        </div>
      )}

      {/* Hold Validation Message */}
      {!canHold && (
        <HoldValidationMessage
          message={
            toTypeCd === 'A'
              ? 'Cannot Hold Request: Start date is less than 1 day'
              : 'Cannot Hold Request: Start date is less than 15 days'
          }
        />
      )}

      {/* Approver Comments */}
      {!showPartialForm && (
        <div className="mb-6 pt-6 pb-6!">
          <label htmlFor="comments" className="block p3 -medium mb-1">
            Approver Comments
          </label>

          <textarea
            id="comments"
            className="w-full border rounded p-2 min-h-20 disabled:bg-gray-100 disabled:cursor-not-allowed"
            maxLength={1000}
            value={approverComments}
            onChange={(e) => setApproverComments(e.target.value)}
            disabled={isDisabledDueToDate || areBothDatesInPast}
          />

          <div className="text-left text-xs text-gray-500 mt-1">
            {1000 - approverComments.length} of 1000 characters left
          </div>
        </div>
      )}

      {/* Buttons */}
      {!showPartialForm && (
        <ButtonGroup
          onCancel={onCancel}
          onSubmit={handleSubmit}
          submitDisabled={submitEnabled ? false : submitDisabled}
        />
      )}
    </div>
  )
}

export default ApprovedTimeOffForm